import React from 'react';
import { redirectUnlessInventory } from '../../utils/moduleAccess';
import { Button, Table, Row, Col, Form, FormGroup, FormControl } from 'react-bootstrap';
import config from '../../../config';
import moment from 'moment';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import {
    ensureSharedTableStyles,
    SHARED_TABLE_SCROLL_WRAP_CLASS,
    SHARED_TABLE_SCROLL_WRAP_PRESERVE_HEADER_COLORS_CLASS,
    SHARED_GRID_TABLE_SCROLL_WRAP_CLASS,
    SHARED_TABLE_HEADER_BG,
} from '../../_components/Shared/dataTableStyles';

class FGWarehouseOccupancyReport extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            fgLocations: [],
            selectedLocation: '',
            selectedRow: '',
            availableRows: [],
            levelData: [],
            gridData: {},
            currentRowIndex: 0,
            totals: {
                goodParts: 0,
                freeLocations: 0,
                onHold: 0,
                overall: 0
            },
            percentages: {
                goodParts: 0,
                freeLocations: 0,
                onHold: 0,
                overall: 100
            },
            occupancyRate: {
                totalLocations: 0,
                percentage: 0
            },
            availableLocations: {
                totalLocations: 0,
                percentage: 0
            },
            reportDate: moment().format('YYYY-MM-DD')
        };
    }

    componentDidMount() {
        ensureSharedTableStyles();
        this.loadFGLocations();
        this.loadReportData();
    }

    loadFGLocations = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.accountId) {
            return;
        }

        fetch(`${config.apiUrl}/getMasterLocations?company=${user.accountId}&area=${encodeURIComponent('FG Warehouse')}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.json())
            .then(data => {
                this.setState({
                    fgLocations: Array.isArray(data) ? data : []
                });
            })
            .catch(error => {
                console.error('Error loading FG locations:', error);
                this.setState({ fgLocations: [] });
            });
    }

    loadReportData = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.accountId) {
            return;
        }

        this.setState({ loading: true });

        let url = `${config.apiUrl}/getFGWarehouseOccupancyReport?company=${user.accountId}`;
        if (this.state.selectedLocation) {
            url += `&locationCode=${encodeURIComponent(this.state.selectedLocation)}`;
        }
        if (this.state.selectedRow) {
            url += `&row=${encodeURIComponent(this.state.selectedRow)}`;
        }

        fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.json())
            .then(data => {
                this.setState({
                    levelData: data.levelData || [],
                    totals: data.totals || this.state.totals,
                    percentages: data.percentages || this.state.percentages,
                    occupancyRate: data.occupancyRate || this.state.occupancyRate,
                    availableLocations: data.availableLocations || this.state.availableLocations,
                    availableRows: data.availableRows || [],
                    gridData: data.gridData || {},
                    currentRowIndex: 0,
                    loading: false
                });
            })
            .catch(error => {
                console.error('Error loading report data:', error);
                this.setState({ loading: false });
            });
    }

    handleLocationChange = (e) => {
        this.setState({ selectedLocation: e.target.value, selectedRow: '' }, () => {
            this.loadReportData();
        });
    }

    handleRowChange = (e) => {
        this.setState({ selectedRow: e.target.value }, () => {
            this.loadReportData();
        });
    }

    handlePrint = () => {
        window.print();
    }

    handleExport = () => {
        alert('Export functionality to be implemented');
    }

    handlePrevRow = () => {
        const { currentRowIndex, availableRows } = this.state;
        if (currentRowIndex > 0) {
            this.setState({ currentRowIndex: currentRowIndex - 1 });
        }
    }

    handleNextRow = () => {
        const { currentRowIndex, availableRows } = this.state;
        if (currentRowIndex < availableRows.length - 1) {
            this.setState({ currentRowIndex: currentRowIndex + 1 });
        }
    }

    renderGrid = () => {
        const { gridData, availableRows, currentRowIndex, selectedLocation } = this.state;
        
        if (!gridData || Object.keys(gridData).length === 0) {
            return <div style={{ textAlign: 'center', padding: '20px' }}>No grid data available</div>;
        }

        const sortedRows = availableRows.length > 0 ? availableRows : Object.keys(gridData).map(Number).sort((a, b) => a - b);
        
        if (sortedRows.length === 0) {
            return <div style={{ textAlign: 'center', padding: '20px' }}>No rows available</div>;
        }
        
        // Ensure currentRowIndex is within bounds
        const safeRowIndex = Math.min(currentRowIndex, sortedRows.length - 1);
        const currentRow = sortedRows[safeRowIndex];
        
        if (currentRow === undefined || !gridData[currentRow]) {
            return <div style={{ textAlign: 'center', padding: '20px' }}>No data for selected row</div>;
        }

        const rowData = gridData[currentRow];
        const levels = Object.keys(rowData).map(Number).sort((a, b) => a - b); // Sort levels ascending (1, 2, 3, 4, 5)
        
        if (levels.length === 0) {
            return <div style={{ textAlign: 'center', padding: '20px' }}>No levels found for this row</div>;
        }

        // Get all pallets across all levels to determine max columns (only from existing cells)
        const allPallets = new Set();
        levels.forEach(level => {
            rowData[level].forEach(cell => {
                allPallets.add(cell.pallet);
            });
        });
        const pallets = Array.from(allPallets).sort((a, b) => a - b);
        const maxPallets = pallets.length > 0 ? Math.max(...pallets) : 0;

        // Determine area prefix (RM or FG)
        const area = 'FG';
        
        // Update state if row index was adjusted
        if (safeRowIndex !== currentRowIndex) {
            setTimeout(() => this.setState({ currentRowIndex: safeRowIndex }), 0);
        }

        return (
            <div style={{ 
                marginTop: '30px', 
                marginBottom: '30px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <h5 style={{ 
                    marginBottom: '20px',
                    color: '#2c3e50',
                    fontWeight: 'bold',
                    fontSize: '18px',
                    textAlign: 'center',
                    paddingBottom: '10px',
                    borderBottom: '2px solid #4990E2'
                }}>
                    Visualization Storage - Row {currentRow}
                </h5>
                <div
                    className={SHARED_GRID_TABLE_SCROLL_WRAP_CLASS}
                    style={{ overflowX: 'auto', backgroundColor: '#fff', borderRadius: '4px' }}
                >
                    <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        border: '1px solid #dee2e6'
                    }}>
                        <thead>
                            <tr>
                                <th style={{ 
                                    padding: '8px', 
                                    border: '1px solid #dee2e6',
                                    backgroundColor: '#f8f9fa',
                                    minWidth: '80px',
                                    textAlign: 'center',
                                    fontWeight: 'bold'
                                }}>Level / Pallet</th>
                                {pallets.map(pallet => (
                                    <th 
                                        key={pallet} 
                                        style={{ 
                                            padding: '8px', 
                                            border: '1px solid #dee2e6',
                                            backgroundColor: '#f8f9fa',
                                            color: '#000000',
                                            width: '80px',
                                            height: '40px',
                                            textAlign: 'center',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {pallet}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {levels.map(level => {
                                const levelPallets = rowData[level] || [];
                                const palletMap = {};
                                levelPallets.forEach(cell => {
                                    palletMap[cell.pallet] = cell;
                                });

                                return (
                                    <tr key={level}>
                                        <td 
                                            style={{ 
                                                padding: '8px', 
                                                border: '1px solid #dee2e6',
                                                backgroundColor: '#f8f9fa',
                                                color: '#000000',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                                width: '120px'
                                            }}
                                        >
                                            Level {level}
                                        </td>
                                        {pallets.map(pallet => {
                                            const cell = palletMap[pallet];
                                            if (!cell) {
                                                // Show empty cell if pallet doesn't exist for this level
                                                return (
                                                    <td
                                                        key={pallet}
                                                        style={{
                                                            padding: '8px',
                                                            border: '1px solid #dee2e6',
                                                            textAlign: 'center',
                                                            width: '80px',
                                                            height: '80px',
                                                            backgroundColor: '#f9f9f9',
                                                            position: 'relative',
                                                            verticalAlign: 'middle'
                                                        }}
                                                    />
                                                );
                                            }
                                            
                                            const label = `${area}${currentRow}.${level}.${pallet}`;
                                            
                                            let backgroundColor = '#FFFFFF';
                                            let textColor = '#000';
                                            
                                            if (cell.status === 'occupied') {
                                                backgroundColor = '#90EE90'; // Green
                                                textColor = '#000';
                                            } else if (cell.status === 'on_hold') {
                                                backgroundColor = '#FF6B6B'; // Red
                                                textColor = '#000';
                                            } else {
                                                // Available (not occupied yet) - white background, black text
                                                backgroundColor = '#FFFFFF'; // White
                                                textColor = '#000'; // Black text
                                            }

                                            return (
                                                <td
                                                    key={pallet}
                                                    style={{
                                                        padding: '8px',
                                                        border: '1px solid #dee2e6',
                                                        textAlign: 'center',
                                                        width: '80px',
                                                        height: '80px',
                                                        backgroundColor: backgroundColor,
                                                        color: textColor,
                                                        position: 'relative',
                                                        verticalAlign: 'middle'
                                                    }}
                                                    title={label}
                                                >
                                                    <div style={{ 
                                                        fontSize: '11px', 
                                                        fontWeight: '500', 
                                                        wordBreak: 'break-word',
                                                        color: textColor
                                                    }}>
                                                        {label}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {sortedRows.length > 1 ? (
                    <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', padding: '10px' }}>
                        <Button
                            variant="secondary"
                            onClick={this.handlePrevRow}
                            disabled={safeRowIndex === 0}
                        >
                            Prev
                        </Button>
                        <span style={{ padding: '5px 15px', fontSize: '14px', fontWeight: '500' }}>
                            Row {safeRowIndex + 1} of {sortedRows.length}
                        </span>
                        <Button
                            variant="secondary"
                            onClick={this.handleNextRow}
                            disabled={safeRowIndex === sortedRows.length - 1}
                        >
                            Next
                        </Button>
                    </div>
                ) : (
                    sortedRows.length === 1 ? (
                        <div style={{ marginTop: '15px', textAlign: 'center', padding: '10px', fontSize: '14px', color: '#666' }}>
                            Showing Row {sortedRows[0]} (1 row total)
                        </div>
                    ) : null
                )}
            </div>
        );
    }

    render() {
        const accessDenied = redirectUnlessInventory(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        const { levelData, totals, percentages, occupancyRate, availableLocations, loading, reportDate, fgLocations, selectedLocation, selectedRow, availableRows } = this.state;

        return (
            <div className="container" style={{ marginTop: '20px', marginBottom: '20px' }}>
                <style type="text/css" media="print">
                    {`
                    @page { size: landscape; margin: 1cm; }
                    .no-print { display: none !important; }
                    `}
                </style>

                <div className="no-print" style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4>FG Warehouse Occupancy Report</h4>
                        <div>
                            <Button variant="primary" onClick={this.loadReportData} disabled={loading} style={{ marginRight: '10px' }}>
                                {loading ? 'Loading...' : 'Refresh'}
                            </Button>
                            <Button variant="success" onClick={this.handlePrint} style={{ marginRight: '10px' }}>
                                Print
                            </Button>
                            <Button variant="info" onClick={this.handleExport}>
                                Export
                            </Button>
                        </div>
                    </div>
                    <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '15px', 
                        borderRadius: '5px', 
                        border: '1px solid #dee2e6',
                        marginBottom: '20px'
                    }}>
                        <Form>
                            <Row className="align-items-end">
                                <Col md={4}>
                                    <FormGroup>
                                        <Form.Label style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                            Filter by FG Location:
                                        </Form.Label>
                                        <FormControl
                                            as="select"
                                            value={selectedLocation}
                                            onChange={this.handleLocationChange}
                                            disabled={loading}
                                            style={{ 
                                                fontSize: '14px',
                                                padding: '8px'
                                            }}
                                        >
                                            <option value="">All FG Locations</option>
                                            {fgLocations.map((location, index) => (
                                                <option key={index} value={location.locationCode}>
                                                    {location.locationCode} - {location.locationDescription || ''}
                                                </option>
                                            ))}
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Form.Label style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                            Filter by Row:
                                        </Form.Label>
                                        <FormControl
                                            as="select"
                                            value={selectedRow}
                                            onChange={this.handleRowChange}
                                            disabled={loading || !selectedLocation}
                                            style={{ 
                                                fontSize: '14px',
                                                padding: '8px'
                                            }}
                                        >
                                            <option value="">All Rows</option>
                                            {availableRows.map((row, index) => (
                                                <option key={index} value={row}>
                                                    Row {row}
                                                </option>
                                            ))}
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                                {(selectedLocation || selectedRow) && (
                                    <Col md={2}>
                                        <Button 
                                            variant="secondary" 
                                            onClick={() => {
                                                this.setState({ selectedLocation: '', selectedRow: '' }, () => {
                                                    this.loadReportData();
                                                });
                                            }}
                                            disabled={loading}
                                            style={{ width: '100%' }}
                                        >
                                            Clear Filter
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </Form>
                    </div>
                </div>

                {loading ? (
                    <DatacoLoadingIndicator loading={loading} label="Loading FG warehouse occupancy report" />
                ) : (
                    <>
                        <div style={{ marginBottom: '30px' }}>
                            <h5 style={{ marginBottom: '15px' }}>FG Warehouse Occupancy Status</h5>
                            <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                                <strong>Date Report Generated: </strong>{reportDate}
                            </div>
                            <div className={SHARED_TABLE_SCROLL_WRAP_PRESERVE_HEADER_COLORS_CLASS}>
                            <Table bordered hover style={{ backgroundColor: '#fff' }}>
                                <thead>
                                    <tr>
                                        <th style={{ backgroundColor: SHARED_TABLE_HEADER_BG, textAlign: 'center', fontWeight: 'bold' }}>Row</th>
                                        <th style={{ backgroundColor: SHARED_TABLE_HEADER_BG, textAlign: 'center', fontWeight: 'bold' }}>Level</th>
                                        <th style={{ backgroundColor: '#90EE90', textAlign: 'center', fontWeight: 'bold' }}>Good Parts</th>
                                        <th style={{ backgroundColor: '#FFFFFF', textAlign: 'center', fontWeight: 'bold', border: '2px solid #000' }}>Free</th>
                                        <th style={{ backgroundColor: '#FF6B6B', textAlign: 'center', fontWeight: 'bold' }}>On Hold</th>
                                        <th style={{ backgroundColor: SHARED_TABLE_HEADER_BG, textAlign: 'center', fontWeight: 'bold' }}>Overall Location</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {levelData.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.row}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.level}</td>
                                            <td style={{ textAlign: 'center' }}>{item.goodParts}</td>
                                            <td style={{ textAlign: 'center' }}>{item.freeLocations}</td>
                                            <td style={{ textAlign: 'center' }}>{item.onHold}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.overall}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ backgroundColor: '#ADD8E6', fontWeight: 'bold' }}>
                                        <td style={{ textAlign: 'center' }} colSpan="2">Total (Qty)</td>
                                        <td style={{ textAlign: 'center' }}>{totals.goodParts}</td>
                                        <td style={{ textAlign: 'center' }}>{totals.freeLocations}</td>
                                        <td style={{ textAlign: 'center' }}>{totals.onHold}</td>
                                        <td style={{ textAlign: 'center' }}>{totals.overall}</td>
                                    </tr>
                                    <tr style={{ backgroundColor: '#ADD8E6', fontWeight: 'bold' }}>
                                        <td style={{ textAlign: 'center' }} colSpan="2">Total (%)</td>
                                        <td style={{ textAlign: 'center' }}>{percentages.goodParts}%</td>
                                        <td style={{ textAlign: 'center' }}>{percentages.freeLocations}%</td>
                                        <td style={{ textAlign: 'center' }}>{percentages.onHold}%</td>
                                        <td style={{ textAlign: 'center' }}>{percentages.overall}%</td>
                                    </tr>
                                </tbody>
                            </Table>
                            </div>
                        </div>

                        <div style={{ marginTop: '30px' }}>
                            <h5 style={{ marginBottom: '15px' }}>FG Warehouse Occupancy Rate</h5>
                            <div className={SHARED_TABLE_SCROLL_WRAP_CLASS}>
                            <Table bordered hover style={{ backgroundColor: '#fff', maxWidth: '600px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'center', fontWeight: 'bold' }}>FG Warehouse Occupancy Rate (%)</th>
                                        <th style={{ textAlign: 'center', fontWeight: 'bold' }}>Total Locations</th>
                                        <th style={{ textAlign: 'center', fontWeight: 'bold' }}>%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ textAlign: 'left', paddingLeft: '15px' }}>Good Parts</td>
                                        <td style={{ textAlign: 'center' }}>{totals.goodParts}</td>
                                        <td style={{ textAlign: 'center' }}>{percentages.goodParts}%</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left', paddingLeft: '15px' }}>Free</td>
                                        <td style={{ textAlign: 'center' }}>{totals.freeLocations}</td>
                                        <td style={{ textAlign: 'center' }}>{percentages.freeLocations}%</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left', paddingLeft: '15px' }}>On Hold</td>
                                        <td style={{ textAlign: 'center' }}>{totals.onHold}</td>
                                        <td style={{ textAlign: 'center' }}>{percentages.onHold}%</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left', paddingLeft: '15px', fontWeight: 'bold' }}>Total</td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{totals.overall}</td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{percentages.overall}%</td>
                                    </tr>
                                    <tr style={{ backgroundColor: '#FFA500', fontWeight: 'bold' }}>
                                        <td style={{ textAlign: 'left', paddingLeft: '15px' }}>Total Occupancy Rate</td>
                                        <td style={{ textAlign: 'center' }}>{occupancyRate.totalLocations}</td>
                                        <td style={{ textAlign: 'center' }}>{occupancyRate.percentage}%</td>
                                    </tr>
                                    <tr style={{ backgroundColor: '#90EE90', fontWeight: 'bold' }}>
                                        <td style={{ textAlign: 'left', paddingLeft: '15px' }}>Available Locations</td>
                                        <td style={{ textAlign: 'center' }}>{availableLocations.totalLocations}</td>
                                        <td style={{ textAlign: 'center' }}>{availableLocations.percentage}%</td>
                                    </tr>
                                </tbody>
                            </Table>
                            </div>
                        </div>

                        {/* Visualization Storage */}
                        {this.renderGrid()}
                    </>
                )}
            </div>
        );
    }
}

export default FGWarehouseOccupancyReport;
